import { ClickAwayListener, FormControl, MenuProps } from '@mui/material';
import { useState } from 'react';
import { CronoMenuItem, CronoSelect } from 'crono-fe-common/components/Select';
import { colors } from 'crono-fe-common/theme';
import { StrategySearchSort } from 'crono-fe-common/types/DTO/strategySearch';
import { renderStrategySortOptions } from 'crono-fe-common/utils';
import React from 'react';

export const STRATEGY_SORT_OPTIONS: StrategySearchSort[] = [
  'UsageDesc',
  'CreatedDesc',
  'Created',
  'Name',
  'NameDesc',
  'Replied',
];

export interface IStrategiesSortPicker {
  sortOption: StrategySearchSort;
  setSortOption: (newValue: StrategySearchSort) => void;
  maxWidth?: number;
  fontSize?: number;
  borderColor?: string;
  MenuProps?: Partial<MenuProps>;
  greenVersion?: boolean;
  //Fits the picker into a flex row alongside other controls: drops the vertical
  //margins, lets flex shrink it below its content width, and ellipsises the label
  //instead of overflowing. Sort labels run to "Creation date (newest first)", which
  //does not fit next to other filters in a narrow popup
  inline?: boolean;
}

const StrategiesSortPicker = ({
  sortOption,
  setSortOption,
  maxWidth,
  fontSize,
  borderColor,
  MenuProps,
  greenVersion,
  inline = false,
}: IStrategiesSortPicker) => {
  const [isOpen, setIsOpen] = useState(false);

  const truncatedLabelSx = inline
    ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }
    : {};

  const sortSelectSx = {
    fontSize: fontSize ? `${fontSize}px` : '14px',
    boxShadow: 'none',
    backgroundColor: colors.white,
    color: greenVersion ? colors.primary : colors.black,
    ...(greenVersion
      ? {
          padding: '0px 28px 0 10px !important',
          '& .MuiSelect-select': {
            padding: '0px !important',
            paddingTop: '0px !important',
            paddingBottom: '0px !important',
            ...truncatedLabelSx,
          },
          '& .MuiInputBase-input': {
            padding: '0px !important',
          },
          border: 'none !important',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
          },
        }
      : {
          padding: '2px',
          '& .MuiSelect-select': truncatedLabelSx,
          '.MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${borderColor ?? colors.grey3}`,
            borderRadius: 2,
          },
        }),
  };

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <FormControl
        sx={{
          mt: inline ? 0 : 1,
          mb: inline ? 0 : 1,
          maxWidth: maxWidth ? `${maxWidth}px` : 'none',
          ...(inline && { minWidth: 0 }),
        }}
        size="small"
      >
        <CronoSelect
          value={sortOption}
          open={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
          onChange={(ev) => {
            setSortOption(ev.target.value as StrategySearchSort);
          }}
          sx={sortSelectSx}
          MenuProps={MenuProps}
        >
          {STRATEGY_SORT_OPTIONS.map((option) => (
            <CronoMenuItem key={option} value={option}>
              {renderStrategySortOptions(option)}
            </CronoMenuItem>
          ))}
        </CronoSelect>
      </FormControl>
    </ClickAwayListener>
  );
};

export default StrategiesSortPicker;
